import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BootShell } from './BootShell';
import { ErrorDisplay } from './ErrorDisplay';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render errors from the routed pages — including a lazy route chunk
 * that fails to load — and shows them in the shell's frame. Without a boundary
 * React unmounts the whole tree on an uncaught error, which leaves `#root`
 * empty: a blank page with nothing to say what went wrong.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route failed to render:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <BootShell>
          <ErrorDisplay
            message={`${error.message}. Reload the page to try again.`}
          />
        </BootShell>
      );
    }
    return this.props.children;
  }
}
